import { useState } from 'react';
import { supabase } from '../supabase/client';

export type PaperFontSize = 'small' | 'medium' | 'large';

const PAPER_FONT_SIZE_KEY = 'paper_font_size_cache';

/** 试卷字号全局设置（exam_settings.font_size_settings JSONB，后台「试卷字号设置」可配） */
export interface PaperFontSizeSettings {
  /** 默认字号档位（用户未手动选择过时生效） */
  defaultSize: PaperFontSize;
  /** 字号递增比例：中档 = 小档 × scaleRatio，大档 = 小档 × scaleRatio²（1~3，默认 1.5） */
  scaleRatio: number;
}

export const PAPER_FONT_SIZE_DEFAULTS: PaperFontSizeSettings = { defaultSize: 'small', scaleRatio: 1.5 };
const SETTINGS_CACHE_KEY = 'paper_font_size_settings_cache';

const isValidSize = (v: unknown): v is PaperFontSize =>
  v === 'small' || v === 'medium' || v === 'large';

function isValidRatio(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v) && v >= 1 && v <= 3;
}

/** 归一化任意来源的设置（含 localStorage 脏值兜底） */
function normalizeSettings(raw: any): PaperFontSizeSettings {
  return {
    defaultSize: isValidSize(raw?.defaultSize) ? raw.defaultSize : PAPER_FONT_SIZE_DEFAULTS.defaultSize,
    scaleRatio: isValidRatio(raw?.scaleRatio) ? raw.scaleRatio : PAPER_FONT_SIZE_DEFAULTS.scaleRatio,
  };
}

/** 模块级内存缓存：启动先用 localStorage（或默认值），init 拉库后覆盖 */
let settingsCache: PaperFontSizeSettings = (() => {
  try {
    const cached = localStorage.getItem(SETTINGS_CACHE_KEY);
    return cached ? normalizeSettings(JSON.parse(cached)) : { ...PAPER_FONT_SIZE_DEFAULTS };
  } catch {
    return { ...PAPER_FONT_SIZE_DEFAULTS };
  }
})();

/** 把缩放倍率写入 :root CSS 变量（.paper-font-medium/.paper-font-large 类通过 calc 消费） */
function applyScaleVars(settings: PaperFontSizeSettings): void {
  try {
    const root = document.documentElement;
    root.style.setProperty('--paper-font-scale-medium', String(settings.scaleRatio));
    root.style.setProperty('--paper-font-scale-large', String(settings.scaleRatio * settings.scaleRatio));
  } catch { /* 非 DOM 环境忽略 */ }
}

/** 同步获取当前字号全局设置（内存缓存，失败回退默认值） */
export function getPaperFontSizeSettings(): PaperFontSizeSettings {
  return settingsCache;
}

/** 本地更新缓存（设置页保存成功后调用，无需重新拉库） */
export function applyPaperFontSizeSettings(settings: PaperFontSizeSettings): void {
  settingsCache = normalizeSettings(settings);
  try {
    localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(settingsCache));
  } catch { /* localStorage 不可用时忽略 */ }
  applyScaleVars(settingsCache);
}

/** 从 exam_settings.font_size_settings 异步加载并更新缓存（App 启动时调用一次；失败静默回退） */
export async function initPaperFontSizeSettings(): Promise<void> {
  // 先用本地缓存立即生效，再异步拉库覆盖
  applyScaleVars(settingsCache);
  try {
    const { data, error } = await supabase
      .from('exam_settings')
      .select('font_size_settings')
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    const raw = data?.font_size_settings;
    if (raw) applyPaperFontSizeSettings(normalizeSettings(raw));
  } catch (err) {
    console.warn('[paperFontSize] 加载试卷字号设置失败（回退缓存/默认值）:', err);
  }
}

/** 读取用户选择的字号档位；用户从未手动选择过时回退后台设置的默认字号 */
export function getPaperFontSize(): PaperFontSize {
  try {
    const cached = localStorage.getItem(PAPER_FONT_SIZE_KEY);
    if (isValidSize(cached)) return cached;
  } catch { /* localStorage 不可用时回退默认字号 */ }
  return settingsCache.defaultSize;
}

/** 持久化字号档位 */
export function setPaperFontSize(size: PaperFontSize): void {
  try {
    localStorage.setItem(PAPER_FONT_SIZE_KEY, size);
  } catch { /* localStorage 不可用时忽略 */ }
}

/** 页面级 Hook：持有字号状态，切换即时生效并持久化 */
export function usePaperFontSize(): [PaperFontSize, (size: PaperFontSize) => void] {
  const [fontSize, setFontSizeState] = useState<PaperFontSize>(getPaperFontSize);
  const setFontSize = (size: PaperFontSize) => {
    setFontSizeState(size);
    setPaperFontSize(size);
  };
  return [fontSize, setFontSize];
}

/** 档位对应的容器 class（paper-font-small 不干预，保持当前默认） */
export const paperFontClass = (size: PaperFontSize): string =>
  size === 'small' ? '' : `paper-font-${size}`;
